import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { ArrowRight, MapPin, Calendar, Phone, Mail, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Portfolio() {
  const [obras, setObras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("");
  const [open, setOpen] = useState(null); // obra abierta en lightbox
  const [fotoIdx, setFotoIdx] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/portfolio/obras`, { params: { limit: 60 } });
        setObras(data.obras || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Tipos únicos para chips
  const tiposDisponibles = useMemo(() => {
    const set = new Set();
    obras.forEach((o) => (o.tipos || []).forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [obras]);

  const filtradas = useMemo(() => {
    if (!filtro) return obras;
    return obras.filter((o) => (o.tipos || []).some((t) => t === filtro));
  }, [obras, filtro]);

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-zinc-100" data-testid="page-portfolio">
      {/* Hero header */}
      <header className="border-b border-zinc-900 bg-gradient-to-b from-zinc-950 to-[#0a0a0c]">
        <div className="max-w-7xl mx-auto px-6 py-12 md:py-20">
          <div className="flex items-center gap-3 mb-4">
            <img src="/brand/logo-rotulaciondnf.png" alt="RotulaciónDNF" className="w-12 h-12 object-contain" />
            <div>
              <div className="text-2xl font-bold tracking-tight">RotulaciónDNF</div>
              <div className="text-xs uppercase tracking-[0.18em] text-amber-400">Producción Gráfica</div>
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-8">
            Nuestro trabajo,<br />
            <span className="text-amber-400">a la vista de todos.</span>
          </h1>
          <p className="text-lg text-zinc-400 max-w-2xl mt-6">
            Galería real de rotulaciones, escaparates, vehículos y eventos que hemos producido.
            Cada imagen es un trabajo terminado para clientes reales en Sevilla y toda España.
          </p>
          <div className="flex items-center gap-4 mt-8 flex-wrap">
            <a href="tel:+34624491271"
              data-testid="btn-llamar"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-zinc-950 font-semibold rounded-lg transition-colors">
              <Phone className="w-4 h-4" /> +34 624 49 12 71
            </a>
            <a href="mailto:administracion@rotulaciondnf.com"
              data-testid="btn-email"
              className="inline-flex items-center gap-2 px-5 py-2.5 border border-zinc-700 hover:bg-zinc-900 text-zinc-100 rounded-lg transition-colors">
              <Mail className="w-4 h-4" /> Pedir presupuesto
            </a>
          </div>
        </div>
      </header>

      {/* Filtros */}
      {tiposDisponibles.length > 0 && (
        <div className="max-w-7xl mx-auto px-6 py-6 border-b border-zinc-900">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setFiltro("")}
              data-testid="filtro-todos"
              className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-full transition-colors ${
                !filtro
                  ? "bg-amber-500 text-zinc-950 font-semibold"
                  : "bg-zinc-900 text-zinc-400 hover:text-zinc-100"
              }`}>
              Todos ({obras.length})
            </button>
            {tiposDisponibles.map((t) => (
              <button
                key={t}
                onClick={() => setFiltro(t)}
                data-testid={`filtro-${t}`}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-full transition-colors ${
                  filtro === t
                    ? "bg-amber-500 text-zinc-950 font-semibold"
                    : "bg-zinc-900 text-zinc-400 hover:text-zinc-100"
                }`}>
                {t}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Grid */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {loading && (
          <div className="text-center text-zinc-500 py-20">Cargando…</div>
        )}
        {!loading && filtradas.length === 0 && (
          <div className="text-center py-20">
            <div className="text-zinc-500 mb-2">Pronto publicaremos aquí nuestros trabajos.</div>
            <a href="mailto:administracion@rotulaciondnf.com" className="text-amber-400 underline text-sm">
              ¿Tienes un proyecto? Cuéntanoslo.
            </a>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtradas.map((o) => (
            <button
              key={o.id}
              data-testid={`obra-${o.id}`}
              onClick={() => { setOpen(o); setFotoIdx(0); }}
              className="group relative bg-zinc-900 rounded-xl overflow-hidden text-left hover:ring-2 hover:ring-amber-500/50 transition-all">
              <div className="aspect-[4/3] overflow-hidden bg-zinc-950">
                <img
                  src={o.fotos[0]}
                  alt={o.titulo}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              {o.fotos.length > 1 && (
                <div className="absolute top-3 right-3 bg-black/70 backdrop-blur text-white text-xs px-2 py-1 rounded">
                  +{o.fotos.length - 1}
                </div>
              )}
              <div className="p-4">
                <div className="font-semibold text-zinc-100 truncate">{o.titulo}</div>
                <div className="flex items-center gap-3 mt-2 text-[11px] text-zinc-500">
                  {o.ciudad && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {o.ciudad}
                    </span>
                  )}
                  {o.fecha && (
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> {o.fecha}
                    </span>
                  )}
                </div>
                {o.tipos && o.tipos.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {o.tipos.map((t) => (
                      <span key={t} className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-amber-500/10 text-amber-300 rounded">
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      </main>

      {/* Lightbox */}
      {open && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
          onClick={() => setOpen(null)}
          data-testid="lightbox">
          <button
            onClick={() => setOpen(null)}
            data-testid="lightbox-close"
            className="absolute top-4 right-4 text-zinc-400 hover:text-white p-2">
            <X className="w-6 h-6" />
          </button>
          <div className="max-w-6xl w-full p-6" onClick={(e) => e.stopPropagation()}>
            <img
              src={open.fotos[fotoIdx]}
              alt={open.titulo}
              className="w-full max-h-[80vh] object-contain rounded-lg"
            />
            <div className="text-center text-zinc-300 mt-4">
              <div className="font-semibold">{open.titulo}</div>
              <div className="text-xs text-zinc-500 mt-1">
                {open.ciudad} · {open.fecha} · Foto {fotoIdx + 1} de {open.fotos.length}
              </div>
            </div>
            {open.fotos.length > 1 && (
              <div className="flex justify-center gap-2 mt-4 overflow-x-auto pb-2">
                {open.fotos.map((f, i) => (
                  <button
                    key={i}
                    onClick={() => setFotoIdx(i)}
                    data-testid={`thumb-${i}`}
                    className={`w-16 h-16 rounded overflow-hidden shrink-0 transition-all ${
                      i === fotoIdx ? "ring-2 ring-amber-500" : "opacity-60 hover:opacity-100"
                    }`}>
                    <img src={f} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* CTA footer */}
      <footer className="border-t border-zinc-900 bg-zinc-950/50 mt-12">
        <div className="max-w-7xl mx-auto px-6 py-12 text-center">
          <h2 className="text-2xl md:text-3xl font-bold">
            ¿Quieres un trabajo así para tu marca?
          </h2>
          <p className="text-zinc-400 mt-3 max-w-xl mx-auto">
            Llámanos o escríbenos. Te pasamos presupuesto sin compromiso.
          </p>
          <div className="flex items-center justify-center gap-4 mt-6 flex-wrap">
            <a href="https://wa.me/34624491271"
              target="_blank"
              rel="noreferrer"
              data-testid="cta-whatsapp"
              className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-zinc-950 font-semibold rounded-lg">
              WhatsApp <ArrowRight className="w-4 h-4" />
            </a>
            <a href="mailto:administracion@rotulaciondnf.com"
              data-testid="cta-email"
              className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-600 text-zinc-950 font-semibold rounded-lg">
              Email <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          <div className="text-xs text-zinc-600 mt-8">
            © {new Date().getFullYear()} RotulaciónDNF · Rotulación Sierra Norte S.L. · B75693010
          </div>
        </div>
      </footer>
    </div>
  );
}
